import ReactMarkdown from "react-markdown";
import { FileText, RefreshCw } from "lucide-react";
import CardImage from "../tool_components/CardImage";
import SummaryCardPDF from "./SummaryCardPdf";
import PdfDownloadButton from "../pdf/PdfDownloadButton";
import useImagePdf from "../pdf/useImagePdf";
import remarkGfm from "remark-gfm";
import { Button, Text } from "@core/index";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "@packages/store/hooks";
import { useState } from "react";
import PdfCustomizationModal from "@features/common/components/pdf-customization-modal";

const SummaryCard = ({
  content,
  imageUrl,
  videoUrl,
  createdAt,
  author,
  onCreateAgain,
}: {
  content: string;
  imageUrl?: string;
  videoUrl?: string;
  createdAt: string;
  author: string;
  onCreateAgain: () => void;
}) => {
  const finalImageUrl = useImagePdf(imageUrl);
  const { t } = useTranslation();
  const [showPdfCustomizationModal, setShowPdfCustomizationModal] =
    useState(false);
  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  );

  return (
    <div className="bg-white dark:bg-dark-600 rounded-xl shadow-sm border border-dark-200 dark:border-dark-400 overflow-hidden">
      <div className="p-4 sm:p-6">
        {imageUrl && (
          <div className="mb-6">
            <CardImage
              imageUrl={imageUrl}
              videoUrl={videoUrl}
              createdAt={createdAt}
            />
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400">
              <FileText size={24} />
            </div>
            <div>
              <Text
                variant="h2"
                className="text-gray-900 dark:text-gray-100 text-lg sm:text-xl lg:text-2xl"
              >
                {t("summaries.details")}
              </Text>
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
                <span>{new Date(createdAt).toLocaleDateString()}</span>
                {author && <span>• {author}</span>}
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            {onCreateAgain && (
              <Button
                onClick={onCreateAgain}
                size="md"
                rounded="full"
                bgColor="bg-teal-500 hover:bg-teal-600 dark:bg-teal-500 dark:hover:bg-teal-600"
                textColor="text-white"
                className="px-4"
              >
                <RefreshCw size={18} />
                <span>{t("common.createAnother")}</span>
              </Button>
            )}

            <PdfDownloadButton
              pdfTitle={content.slice(0, 20)}
              openCustomizationModal={() => setShowPdfCustomizationModal(true)}
              document={
                <SummaryCardPDF
                  customizationSettings={customizationSettings}
                  content={content}
                  createdAt={createdAt}
                  author={author}
                  imageUrl={finalImageUrl}
                />
              }
              btnTheme="bg-teal-500 hover:bg-teal-600 dark:bg-teal-500 dark:hover:bg-teal-600"
            />
          </div>
        </div>

        <div className="prose dark:prose-invert max-w-none">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            className="prose dark:prose-invert max-w-none prose-headings:text-gray-900 dark:prose-headings:text-gray-100 prose-headings:font-semibold prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-p:leading-relaxed prose-strong:text-gray-900 dark:prose-strong:text-gray-100 prose-strong:font-semibold prose-ul:text-gray-700 dark:prose-ul:text-gray-300 prose-ol:text-gray-700 dark:prose-ol:text-gray-300 prose-li:text-gray-700 dark:prose-li:text-gray-300 prose-blockquote:border-l-teal-500 dark:prose-blockquote:border-l-teal-400 prose-blockquote:bg-gray-50 dark:prose-blockquote:bg-dark-500 prose-blockquote:text-gray-700 dark:prose-blockquote:text-gray-300 prose-code:bg-gray-100 dark:prose-code:bg-dark-500 prose-code:text-gray-800 dark:prose-code:text-gray-200 prose-pre:bg-gray-100 dark:prose-pre:bg-dark-500 prose-pre:text-gray-800 dark:prose-pre:text-gray-200 prose-table:border-collapse prose-td:border prose-td:border-gray-300 dark:prose-td:border-gray-600 prose-td:p-2 prose-th:border prose-th:border-gray-300 dark:prose-th:border-gray-600 prose-th:p-2 prose-th:bg-gray-50 dark:prose-th:bg-dark-500 prose-th:text-gray-900 dark:prose-th:text-gray-100 prose-table:w-full prose-table:my-4 prose-pre:whitespace-pre-wrap prose-pre:break-words prose-pre:overflow-auto prose-pre:max-w-full prose-pre:rounded-lg prose-pre:p-4"
            data-testid="summary-content"
          >
            {content}
          </ReactMarkdown>
        </div>
      </div>

      {/* PDF Customization Modal */}
      <PdfCustomizationModal
        show={showPdfCustomizationModal}
        onClose={() => setShowPdfCustomizationModal(false)}
        hideSchoolLogoOptions
        documentComponent={
          <SummaryCardPDF
            customizationSettings={customizationSettings}
            content={content}
            createdAt={createdAt}
            author={author}
            imageUrl={finalImageUrl}
          />
        }
      />
    </div>
  );
};

export default SummaryCard;
